import React, { useState } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogPositioner,
  Portal,
  Button,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Box,
  SimpleGrid,
} from '@chakra-ui/react';
import { track, Events } from '../utils/analytics';

const ROOM_USAGE_OPTIONS = [
  { value: 'new_release', label: "I'm using my room to promote a new release" },
  { value: 'personal_website', label: "I'm using my room as a personal website" },
  { value: 'shop', label: "I'm using my room as a shop" },
  { value: 'art_piece', label: "I'm using my room as an art piece" },
  { value: 'for_fun', label: "I'm just making a room for fun" },
  { value: 'other', label: 'Other' },
];

const IDENTITY_OPTIONS = [
  { value: 'musician', label: 'I am a musician' },
  { value: 'photographer', label: 'I am a photographer' },
  { value: 'visual_artist', label: 'I am a visual artist' },
  { value: 'business_owner', label: 'I am a business owner' },
  { value: 'programmer', label: 'I am a programmer' },
  { value: 'other', label: 'Other' },
];

const OptionButton = ({ label, isSelected, onClick }) => (
  <Box
    px={{ base: 1.5, md: 3 }}
    py={{ base: 1, md: 3 }}
    border={{ base: '1.5px solid', md: '2px solid' }}
    borderColor={isSelected ? 'blue.500' : 'gray.200'}
    bg={isSelected ? 'blue.50' : 'white'}
    borderRadius="md"
    cursor="pointer"
    onClick={onClick}
    _hover={{ borderColor: isSelected ? 'blue.500' : 'blue.300' }}
    transition="all 0.15s ease"
    userSelect="none"
  >
    <Text fontSize={{ base: '2xs', md: 'sm' }} color={isSelected ? 'blue.700' : 'gray.700'}>
      {label}
    </Text>
  </Box>
);

const OnboardingSurveyModal = ({ isOpen, onDismiss }) => {
  const [roomUsage, setRoomUsage] = useState(null);
  const [roomUsageOther, setRoomUsageOther] = useState('');
  const [identity, setIdentity] = useState(null);
  const [identityOther, setIdentityOther] = useState('');
  const [otherComments, setOtherComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const surveyData = {
        roomUsage: roomUsage === 'other' ? `other: ${roomUsageOther}` : roomUsage,
        roomUsageOther: roomUsage === 'other' ? roomUsageOther : null,
        identity: identity === 'other' ? `other: ${identityOther}` : identity,
        identityOther: identity === 'other' ? identityOther : null,
        otherComments: otherComments || null,
        completedAt: new Date().toISOString(),
      };
      await onDismiss(surveyData);
      track(Events.ONBOARDING_SURVEY_COMPLETED, {
        roomUsage: surveyData.roomUsage,
        identity: surveyData.identity,
      });
    } catch (error) {
      console.error('Failed to submit survey:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      await onDismiss(null);
      track(Events.ONBOARDING_SURVEY_SKIPPED);
    } catch (error) {
      console.error('Failed to skip survey:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSelection = roomUsage || identity || otherComments;

  return (
    <Portal>
      <Dialog.Root open={isOpen} onOpenChange={() => {}}>
        <DialogBackdrop />
        <DialogPositioner alignItems={{ base: 'flex-start', md: 'center' }} pt={{ base: 4, md: 0 }}>
          <DialogContent
            maxW={{ base: '70%', md: 'lg' }}
            mx={4}
            p={{ base: 3, md: 6 }}
          >
            <DialogHeader py={{ base: 1, md: undefined }}>
              <DialogTitle>
                <Text fontSize={{ base: 'sm', md: 'xl' }}>Welcome to Mogulplex!</Text>
              </DialogTitle>
            </DialogHeader>

            <DialogBody pb={{ base: 3, md: 6 }}>
              <VStack spacing={{ base: 3, md: 5 }} align="stretch">
                <Text color="gray.600" fontSize={{ base: 'xs', md: 'sm' }}>
                  Help us by answering a few questions or press skip to make a room.
                </Text>

                {/* Question 1 */}
                <Box>
                  <Text fontWeight="semibold" fontSize={{ base: 'xs', md: undefined }} mb={{ base: 2, md: 3 }}>
                    What will you use your room for?
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 1.5, md: 2 }}>
                    {ROOM_USAGE_OPTIONS.map((option) => (
                      <OptionButton
                        key={option.value}
                        label={option.label}
                        isSelected={roomUsage === option.value}
                        onClick={() => setRoomUsage(option.value)}
                      />
                    ))}
                  </SimpleGrid>
                  {roomUsage === 'other' && (
                    <Input
                      mt={2}
                      placeholder="Tell us more..."
                      value={roomUsageOther}
                      onChange={(e) => setRoomUsageOther(e.target.value)}
                      size={{ base: 'xs', md: 'sm' }}
                      autoFocus
                    />
                  )}
                </Box>

                {/* Question 2 */}
                <Box>
                  <Text fontWeight="semibold" fontSize={{ base: 'xs', md: undefined }} mb={{ base: 2, md: 3 }}>
                    Do you consider yourself an artist?
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 1.5, md: 2 }}>
                    {IDENTITY_OPTIONS.map((option) => (
                      <OptionButton
                        key={option.value}
                        label={option.label}
                        isSelected={identity === option.value}
                        onClick={() => setIdentity(option.value)}
                      />
                    ))}
                  </SimpleGrid>
                  {identity === 'other' && (
                    <Input
                      mt={2}
                      placeholder="Tell us more..."
                      value={identityOther}
                      onChange={(e) => setIdentityOther(e.target.value)}
                      size={{ base: 'xs', md: 'sm' }}
                      autoFocus
                    />
                  )}
                </Box>

                {/* Question 3 */}
                <Box>
                  <Text fontWeight="semibold" fontSize={{ base: 'xs', md: undefined }} mb={{ base: 2, md: 3 }}>
                    Other comments
                  </Text>
                  <Textarea
                    placeholder="Anything else you'd like to share..."
                    value={otherComments}
                    onChange={(e) => setOtherComments(e.target.value)}
                    size={{ base: 'xs', md: 'sm' }}
                    rows={{ base: 2, md: 3 }}
                  />
                </Box>

                {/* Actions */}
                <HStack justify="space-between" pt={{ base: 1, md: 2 }}>
                  <Button
                    variant="ghost"
                    onClick={handleSkip}
                    disabled={isSubmitting}
                    size={{ base: 'xs', md: 'sm' }}
                  >
                    Skip
                  </Button>
                  <Button
                    colorPalette="blue"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={!hasSelection || isSubmitting}
                    size={{ base: 'xs', md: 'sm' }}
                  >
                    Submit
                  </Button>
                </HStack>
              </VStack>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Dialog.Root>
    </Portal>
  );
};

export default OnboardingSurveyModal;
